import "./ClaimLinkingDrawerTable.scss";

import cx from "classnames";
import KaboodleToaster from "components/common/KaboodleToaster";
import SearchBar from "components/SearchBar/SearchBar";
import Table from "components/Table/Table";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useState } from "react";
import { setShowClaimLinkingDrawer } from "reducers/drawer";
import { useGetClaimsQuery } from "services/claims-api";
import {
  useCreateFileClaimAssociationMutation,
  useDeleteFileClaimAssociationMutation,
  useGetSuggestedClaimAssociationsQuery,
} from "services/file-api.ts";
import type { ClaimsDataType, FileType } from "types/DataTypes";
import { useQuery } from "utils/hooks";
import { useDispatch, useSelector } from "utils/redux";
import { GLOBAL_SEARCH_PARAM, useUrlStore } from "utils/useUrlStore.ts";

type ClaimLinkingProps = {
  fileData?: FileType;
};

type ClaimType = {
  id: number;
  rre_name: string;
  rre_id: number;
  first_name: string | null;
  last_name: string | null;
  ssn: string | null;
  dob: string | null;
  last_query_send_date: string | null;
  is_medicare_eligible: boolean;
  suggested_for_file?: boolean;
};

const ClaimLinking = ({ fileData }: ClaimLinkingProps) => {
  const dispatch = useDispatch();
  const query = useQuery();
  const { getFilters } = useUrlStore();
  const filters = getFilters();
  const [page, setPage] = useState(1);

  const [createFileClaimAssociation] = useCreateFileClaimAssociationMutation();
  const [deleteFileClaimAssociation] = useDeleteFileClaimAssociationMutation();

  const {
    data: claims = { results: [], count: 0, next: null, previous: null },
    isFetching,
  } = useGetClaimsQuery(
    {
      filters: {
        ...filters,
        limit: PAGE_SIZE.toString(),
        offset: OFFSET(page).toString(),
      },
      searchTerm: query.get(GLOBAL_SEARCH_PARAM) || undefined,
    },
    { skip: false },
  );

  const { data: suggestedClaims } = useGetSuggestedClaimAssociationsQuery(
    {
      id: fileData?.id || 0,
    },
    { skip: !fileData?.id },
  );

  const associatedClaimIds: number[] =
    fileData?.claim_associations?.map((ass) => ass.claim) || [];

  const showClaimLinkingDrawer = useSelector(
    (state) => state.drawer.showClaimLinkingDrawer,
  );

  const handleUnlink = async (claim: ClaimType) => {
    try {
      await deleteFileClaimAssociation({
        id: fileData?.id,
        claimId: claim.id,
      }).unwrap();
      KaboodleToaster.success("Claim unlinked successfully");
    } catch (error) {
      console.error(error);
      KaboodleToaster.error("Failed to unlink claim with file.");
    }
  };

  const handleLink = async (claim: ClaimType) => {
    try {
      await createFileClaimAssociation({
        id: fileData?.id,
        claimId: claim.id,
      }).unwrap();
      KaboodleToaster.success(`Claim linked successfully`);
    } catch (error) {
      console.error(error);
      KaboodleToaster.error("Failed to link claim with file.");
    }
  };

  const suggestedClaimIds = suggestedClaims?.map((claim) => claim.id);

  const filteredClaims = claims.results?.filter((claim) => {
    return suggestedClaimIds?.indexOf(claim.id) === -1;
  });

  const suggestedRows: ClaimType[] =
    suggestedClaims?.map((claim: ClaimsDataType) => ({
      id: claim.id,
      rre_name: claim.rre_name,
      rre_id: claim.rre_rre_id,
      first_name: claim.injured_party_first_name,
      last_name: claim.injured_party_last_name,
      ssn: claim.injured_party_ssn,
      dob: claim.injured_party_dob,
      last_query_send_date: claim.last_query_send_date,
      is_medicare_eligible: claim.is_medicare_beneficiary_verbose === "true",
      suggested_for_file: true,
    })) || [];

  const otherRows: ClaimType[] =
    filteredClaims?.map((claim: ClaimsDataType) => ({
      id: claim.id,
      rre_name: claim.rre_name,
      rre_id: claim.rre_rre_id,
      first_name: claim.injured_party_first_name,
      last_name: claim.injured_party_last_name,
      ssn: claim.injured_party_ssn,
      dob: claim.injured_party_dob,
      last_query_send_date: claim.last_query_send_date,
      is_medicare_eligible: claim.is_medicare_beneficiary === true,
    })) || [];

  const rows: ClaimType[] = [...suggestedRows, ...otherRows];

  const columns = [
    {
      key: "id",
      name: "ID",
      renderCell: (_props: { row: ClaimType }) => {
        if (_props.row?.suggested_for_file) {
          return (
            <>
              {_props.row.id}
              <div
                className="material-icons txt-color purple pd-left"
                title="Suggested"
              >
                assistant
              </div>
            </>
          );
        } else {
          return <div>{_props.row.id}</div>;
        }
      },
      width: "150px",
    },
    {
      key: "rre_id",
      name: "RRE ID",
      ...commonColumnProps,
    },
    {
      key: "rre_name",
      name: "RRE Name",
      ...commonColumnProps,
    },
    {
      key: "first_name",
      name: "First Name",
      ...commonColumnProps,
    },
    {
      key: "last_name",
      name: "Last Name",
      ...commonColumnProps,
    },
    {
      key: "ssn",
      name: "SSN",
      ...commonColumnProps,
    },
    {
      key: "dob",
      name: "DOB",
      ...commonColumnProps,
    },
    {
      key: "last_query_send_date",
      name: "Last Query Sent",
      ...commonColumnProps,
    },
    {
      key: "is_medicare_eligible",
      name: "Medicare Eligible",
      ...commonColumnProps,
    },
    {
      key: "actions",
      name: "Actions",
      width: "150px",
      renderCell: (_props: { row: ClaimType }) => {
        const isAssociated = associatedClaimIds?.includes(_props.row.id);

        const viewClaimButton = (
          <div
            className="material-icons"
            title="View Claim"
            onClick={(e) => {
              e.preventDefault();
              window.open(
                `/section-111/claims/${_props.row.id}/overview/`,
                "_blank",
              );
            }}
          >
            zoom_in
          </div>
        );

        if (isAssociated) {
          return (
            <div className="flex jc-se no-pd action-buttons">
              {viewClaimButton}
              <div
                className="material-icons unlink"
                title="Unlink"
                onClick={(e) => {
                  e.preventDefault();
                  handleUnlink(_props.row);
                }}
              ></div>
            </div>
          );
        } else {
          return (
            <div className="flex jc-se no-pd action-buttons">
              {viewClaimButton}
              <div
                className="material-icons add_link"
                title="Link"
                onClick={(e) => {
                  e.preventDefault();
                  handleLink(_props.row);
                }}
              >
                add_link
              </div>
            </div>
          );
        }
      },
    },
  ];

  return (
    <div
      className={cx("documents documents-modal flex jc-se", {
        open: showClaimLinkingDrawer,
      })}
    >
      <div className="documents-wrapper w-search">
        <div className="header-primary flex jc-sb">
          Claims
          <a
            className="header-primary alt-btn"
            onClick={() => dispatch(setShowClaimLinkingDrawer(false))}
          >
            Close
          </a>
        </div>

        <div className="search-container">
          <SearchBar override={true} filterType="claims" />
        </div>

        <div className="data-values mrg-top">
          <Table
            className="claim-linking-table"
            drawer={true}
            columns={columns}
            rows={rows}
            rowKeyGetter={(row: ClaimType) => row.id}
            emptyTableMessage="No claims available"
            onCellClick={() => {}}
            isLoading={isFetching}
            pagination={{
              pageSize: PAGE_SIZE,
              pageIndex: page,
              rowCount: claims.count,
              onPageChange: setPage,
              isLoading: isFetching,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ClaimLinking;
